import { type PropsWithChildren, type ReactNode, createContext, useMemo } from 'react';

import type { YMapsApi, UseYMapsProps } from '../../lib/types';
import { useYMaps } from '../../lib/useYMaps';
import { MapInitStatus } from '../../lib/constants';

/** Параметры для контекста подключения карт */
export interface YMapsProviderProps extends PropsWithChildren<UseYMapsProps> {
    /** Компонент для отображения во время инициализации */
    loadingFallback?: ReactNode;
    /** Компонент для отображения в случае возниковения ошибки */
    errorFallback?: ReactNode;
}

/**
 * Контекст, предоставляющий доступ к компонентам карт
 */
export const YandexMapsApiContext = createContext<YMapsApi>({
    maps: null,
    status: MapInitStatus.NOT_INITIALIZED,
    error: null,
    addons: null,
    reactify: null,
});

/**
 * Провайдер, осуществляющий подключение яндекс карт
 * @kind component
 */
export const YandexMapsApiProvider = ({
    loadingFallback,
    errorFallback,
    children,
    ...hookProps
}: YMapsProviderProps) => {
    const contextValue = useYMaps(hookProps);

    const child = useMemo(() => {
        if (errorFallback && MapInitStatus.ERROR === contextValue.status) {
            return errorFallback;
        }

        if (
            loadingFallback &&
            (MapInitStatus.NOT_INITIALIZED === contextValue.status || MapInitStatus.PENDING === contextValue.status)
        ) {
            return loadingFallback;
        }

        return children;
    }, [children, contextValue.status, errorFallback, loadingFallback]);

    return <YandexMapsApiContext.Provider value={contextValue}>{child}</YandexMapsApiContext.Provider>;
};
